import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { User, Plus, Settings, LogIn, Bot, CreditCard, History, BarChart3 } from 'lucide-react'
import { componentClasses, cn } from '../utils/designSystem'
import { useAuth } from '../hooks/useSupabaseAuth.jsx'
import UserProfile from './UserProfile'

const Sidebar = React.memo(({ onNavigate, isMobileMenuOpen, onAuthModalOpen }) => {
  const navigate = useNavigate()
  const { user, loading } = useAuth()
  
  const menuItems = [
    { icon: Plus, label: 'Home', path: '/' },
    { icon: History, label: 'Agent History', path: '/agent-history' },
    { icon: BarChart3, label: 'Usage', path: '/usage' },
    { icon: CreditCard, label: 'Subscription', path: '/subscription' },
  ]

  const mobileMenuItems = [
    { icon: null, label: 'Home', path: '/', isCenter: true, isLogo: true },
    { icon: History, label: 'History', path: '/agent-history' },
    { icon: BarChart3, label: 'Usage', path: '/usage' },
    { icon: CreditCard, label: 'Subscription', path: '/subscription' },
    { icon: user ? User : LogIn, label: user ? 'Profile' : 'Login', path: null, isAuth: true },
  ]

  const handleNavigation = (path) => {
    navigate(path)
    if (onNavigate) {
      onNavigate()
    }
  }

  const handleAuthClick = () => {
    onAuthModalOpen()
  }

  const [showMobileProfile, setShowMobileProfile] = useState(false)

  const handleMobileItemClick = (item) => {
    if (item.isAuth) {
      if (!user) {
        handleAuthClick()
      } else {
        setShowMobileProfile(true)
      }
    } else if (item.path) {
      handleNavigation(item.path)
    }
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <div className={cn(
        componentClasses.sidebar,
        "hidden md:flex fixed top-0 left-0 w-16 h-screen flex-col items-center py-4 border-r border-border-primary"
      )} style={{backgroundColor: '#232323'}}>
        {/* Logo */}
        <div className="mb-6">
          <button
            onClick={() => handleNavigation('/')}
            className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors group"
            title="Home"
          >
            <img 
              src="https://media.cara.id/images/logo-cara-white.png" 
              alt="Cara Logo" 
              className="w-8 h-8 object-contain"
            />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="flex flex-col space-y-2 mb-auto">
          {menuItems.map((item, index) => {
            const IconComponent = item.icon
            return (
              <button
                key={index}
                onClick={() => handleNavigation(item.path)}
                className={componentClasses.navItemInactive}
                title={item.label}
              >
                <IconComponent className="w-5 h-5" />
              </button>
            )
          })}
        </div>

        {/* User Section */}
        <div className="flex flex-col items-center space-y-2">
          {!loading && (
            user ? (
              <UserProfile />
            ) : (
              <button
                onClick={handleAuthClick}
                className={componentClasses.navItemInactive}
                title="Login"
              >
                <LogIn className="w-5 h-5" />
              </button>
            )
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className={cn(
        "md:hidden fixed bottom-0 left-0 right-0 bg-bg-secondary border-t border-border-primary",
        isMobileMenuOpen ? "z-40" : "z-30"
      )} style={{backgroundColor: '#232323'}}>
        <div className="flex items-center justify-around px-2 py-2">
          {mobileMenuItems.map((item, index) => {
            const IconComponent = item.icon
            return (
              <button
                key={index}
                onClick={() => handleMobileItemClick(item)}
                className={cn(
                  "flex flex-col items-center justify-center p-1 transition-colors min-h-[60px] flex-1",
                  item.isCenter 
                    ? "text-text-primary" 
                    : "text-text-tertiary hover:text-text-primary"
                )}
                title={item.label}
              >
                <div className="flex items-center justify-center w-6 h-6 mb-1">
                  {item.isLogo ? (
                    <img 
                      src="https://media.cara.id/images/logo-cara-white.png" 
                      alt="Cara Logo" 
                      className="w-5 h-5 object-contain"
                    />
                  ) : item.isAuth && user ? (
                    <div className="w-5 h-5 rounded-full overflow-hidden">
                      {user.user_metadata?.avatar_url || user.user_metadata?.picture ? (
                        <img 
                          src={user.user_metadata?.avatar_url || user.user_metadata?.picture} 
                          alt="Profile"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'flex'
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-full h-full bg-accent-primary flex items-center justify-center ${
                          (user.user_metadata?.avatar_url || user.user_metadata?.picture) ? 'hidden' : 'flex'
                        }`}
                      >
                        <span className="text-white text-xs font-medium">
                          {(user.user_metadata?.full_name || user.email?.split('@')[0] || 'User')
                            .split(' ')
                            .map(name => name[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <IconComponent className="w-5 h-5" />
                  )}
                </div>
                <span className="text-xs text-center leading-tight truncate max-w-full">{item.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Mobile Profile Modal */}
      {showMobileProfile && user && (
        <div className="md:hidden">
          <UserProfile onModalClose={() => setShowMobileProfile(false)} />
        </div>
      )}
    </>
  )
})

Sidebar.propTypes = {
  onNavigate: PropTypes.func,
  isMobileMenuOpen: PropTypes.bool,
  onAuthModalOpen: PropTypes.func
}

Sidebar.displayName = 'Sidebar'

export default Sidebar
